import * as React from 'react';

import { enumCurrencyCode } from '@rematter/frontend-lib-api-client';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftLineItems,
  $effectiveLineItems,
  $remoteInvoiceDetail,
} from '../../_state/invoiceDetail.state';
import { InvoiceRecord } from '../../types';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';
import { FinanceLineItemTable } from '../_shared/FinanceLineItemTable/FinanceLineItemTable';
import { FinanceLineItemData } from '../_shared/FinanceLineItemTable/types';

import { InvoiceDetailFooter } from './InvoiceDetailFooter';

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ INVOICE TAB LINE ITEMS ⚛️ ⚛️ ⚛️
//
export const InvoiceTabLineItems: React.FC<Props> = ({ disabled = false }) => {
  // Atoms
  const effectiveLineItems = useAtomValue($effectiveLineItems);
  const setDraftLineItems = useSetAtom($currentDraftLineItems);
  const queryResult = useAtomValue($remoteInvoiceDetail);
  const invoice = (
    queryResult?.data as { invoice: InvoiceRecord | null } | undefined
  )?.invoice;

  const currencyCode = invoice?.amount?.currencyCode ?? enumCurrencyCode.USD;

  // Callbacks
  const handleUpdateLineItem = React.useCallback(
    (id: string, updates: Partial<FinanceLineItemData>) => {
      setDraftLineItems(
        effectiveLineItems.map((item) =>
          item.id === id ? { ...item, ...updates } : item,
        ),
      );
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleDeleteLineItem = React.useCallback(
    (id: string) => {
      setDraftLineItems(effectiveLineItems.filter((item) => item.id !== id));
    },
    [effectiveLineItems, setDraftLineItems],
  );

  const handleAddLineItem = React.useCallback(() => {
    const maxSortOrder = effectiveLineItems.reduce(
      (max, item) => Math.max(max, item.sortOrder),
      -1,
    );
    const newItem: FinanceLineItemData = {
      id: `temp-${Date.now()}-${Math.random()}`,
      description: '',
      quantity: null,
      unit: null,
      unitPrice: null,
      totalPrice: null,
      itemId: null,
      groupName: null,
      sortOrder: maxSortOrder + 1,
    };
    setDraftLineItems([...effectiveLineItems, newItem]);
  }, [effectiveLineItems, setDraftLineItems]);

  return (
    <FinanceDetailLayout
      className="@InvoiceTabLineItems h-auto"
      footer={<InvoiceDetailFooter />}
    >
      <FinanceLineItemTable
        lineItems={effectiveLineItems}
        onUpdateLineItem={handleUpdateLineItem}
        onDeleteLineItem={handleDeleteLineItem}
        onAddLineItem={handleAddLineItem}
        currencyCode={currencyCode}
        disabled={disabled}
      />
    </FinanceDetailLayout>
  );
};
